import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";

/**
 * ProductSpinner — frame-sequence playback that simulates a gentle
 * "showroom turntable" rotation of the hero product.
 *
 * Today the frames are 24 perspective-warped variants of a single
 * product photo (subtle ±14° sway). If real multi-angle photos are
 * later dropped into `src/assets/dokra-frames/` (frame-00.png …
 * frame-NN.png) using the same naming, the component will play them
 * as-is — giving a true 360°.
 *
 * Behaviour:
 * - Auto-plays continuously
 * - Pauses on hover
 * - Click + horizontal drag scrubs frames manually
 * - Honours prefers-reduced-motion (shows static frame-00)
 */

// Eagerly import all frames as URLs so Vite bundles them.
const frameModules = import.meta.glob<string>(
  "@/assets/dokra-frames/frame-*.png",
  { eager: true, query: "?url", import: "default" }
);

const FRAMES: string[] = Object.keys(frameModules)
  .sort()
  .map((k) => frameModules[k] as unknown as string);

interface ProductSpinnerProps {
  className?: string;
  /** Milliseconds per full loop. Lower = faster. */
  loopMs?: number;
  /** Allow click-drag to scrub (true in hero, false in travelling overlay) */
  interactive?: boolean;
  /** Pause auto-rotation entirely (used while travelling between sections) */
  paused?: boolean;
}

const ProductSpinner = ({
  className = "",
  loopMs = 6000,
  interactive = true,
  paused = false,
}: ProductSpinnerProps) => {
  const reduced = useReducedMotion();
  const [frame, setFrame] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragStartX = useRef(0);
  const dragStartFrame = useRef(0);
  const rafRef = useRef<number | null>(null);

  // Auto-rotate
  useEffect(() => {
    if (reduced || paused || dragging || FRAMES.length === 0) return;
    let last = performance.now();
    let acc = 0;
    const perFrame = loopMs / FRAMES.length;
    const tick = (now: number) => {
      const dt = now - last;
      last = now;
      acc += dt;
      while (acc >= perFrame) {
        acc -= perFrame;
        setFrame((f) => (f + 1) % FRAMES.length);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [reduced, paused, dragging, loopMs]);

  // Drag handlers
  const onPointerDown = (e: React.PointerEvent) => {
    if (!interactive) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setDragging(true);
    dragStartX.current = e.clientX;
    dragStartFrame.current = frame;
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging) return;
    const dx = e.clientX - dragStartX.current;
    // 8px of drag = 1 frame
    const delta = Math.round(dx / 8);
    const next =
      ((dragStartFrame.current + delta) % FRAMES.length + FRAMES.length) %
      FRAMES.length;
    setFrame(next);
  };
  const onPointerUp = (e: React.PointerEvent) => {
    if (!interactive) return;
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    setDragging(false);
  };

  if (FRAMES.length === 0) return null;

  return (
    <div
      className={`relative w-full h-full ${interactive ? "cursor-grab active:cursor-grabbing" : ""} ${className}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      role="img"
      aria-label="Dokra brass figurine, rotating view"
    >
      {/* Render all frames stacked; toggle opacity to avoid load flicker */}
      {FRAMES.map((src, i) => (
        <img
          key={src}
          src={src}
          alt=""
          aria-hidden="true"
          draggable={false}
          width={760}
          height={1130}
          {...(i === 0
            ? { fetchPriority: "high" as const, loading: "eager" as const }
            : { loading: "lazy" as const })}
          className="absolute inset-0 w-full h-full object-contain select-none pointer-events-none"
          style={{
            opacity: i === frame ? 1 : 0,
            transformOrigin: "50% 86%",
            filter:
              "drop-shadow(0 22px 28px hsl(var(--earth-brown) / 0.35)) drop-shadow(0 6px 8px hsl(var(--earth-brown) / 0.25))",
          }}
        />
      ))}
    </div>
  );
};

export default ProductSpinner;
